;(function () {
  // Interactive caliber -> recommended hole size control for loading-block PDPs.
  //
  // Data (assets/caliber-specs.json) shape:
  //   { hole_sizes: [{ name, hole_diameter, max_diameter, variant_value? }, ...],
  //     low_profile:      [{ cartridge, base_diameter, ... }, ...],
  //     standard_profile: [ ... ] }
  //
  // max_diameter is the largest case head/rim the bucket holds and is what the
  // lookup uses; hole_diameter is the printed hole (max_diameter + clearance) and
  // is informational here — change the clearance without touching the buckets.
  //
  // Rule: recommend the smallest hole whose max_diameter >= the cartridge's
  // effective base diameter — max(base_diameter, rim_diameter), since rimmed
  // cartridges are widest at the rim. Some diameter values are strings holding one
  // or more numbers (e.g. "0.5820, 0.6040"); we parse every number out and use the
  // largest.
  //
  // Having found the hole, the control also selects the matching variant in the
  // product's variant picker, so the customer doesn't have to carry the answer
  // over by hand. See autoSelect.

  // Cache the fetch so multiple instances / specs sets share one request per URL.
  const specsCache = new Map()

  function loadSpecs(url) {
    if (!specsCache.has(url)) {
      specsCache.set(
        url,
        fetch(url).then(function (response) {
          if (!response.ok) throw new Error('caliber-specs fetch failed: ' + response.status)
          return response.json()
        }),
      )
    }
    return specsCache.get(url)
  }

  // Pull the effective (largest) diameter out of a number or a messy string.
  function effectiveDiameter(value) {
    if (typeof value === 'number') return value
    if (typeof value !== 'string') return NaN
    const numbers = (value.match(/[\d.]+/g) || []).map(parseFloat).filter(function (n) {
      return !isNaN(n)
    })
    return numbers.length ? Math.max.apply(null, numbers) : NaN
  }

  // The diameter that has to clear the loading-block hole: the larger of the
  // case-head (base) and rim diameters. Rimmed cartridges (rim_diameter present)
  // are widest at the rim; rimless ones omit it and are widest at the base.
  function effectiveBase(entry) {
    const base = effectiveDiameter(entry.base_diameter)
    const rim = effectiveDiameter(entry.rim_diameter)
    if (isNaN(rim)) return base
    if (isNaN(base)) return rim
    return Math.max(base, rim)
  }

  // NaN-aware ascending compare of two numbers. NaN (unparseable/missing) sorts
  // to the end so a bad value never wins. Returns 0 when equal, letting the
  // caller fall through to the next sort dimension.
  function compareNumbers(da, db) {
    const aNaN = isNaN(da)
    const bNaN = isNaN(db)
    if (aNaN && bNaN) return 0
    if (aNaN) return 1
    if (bNaN) return -1
    return da - db
  }

  // Compare a sort dimension, parsing messy string values via effectiveDiameter.
  function compareDimension(a, b) {
    return compareNumbers(effectiveDiameter(a), effectiveDiameter(b))
  }

  // Each control lists exactly one section of the data, named by the block's
  // caliber_type setting.
  //
  // 'pistol' and 'rifle' are what those sections used to be called and are still
  // accepted. That setting is stored per theme in the Shopify admin, so a theme
  // that hasn't been re-pushed since the rename still holds the old value —
  // dropping it would quietly serve such a page the other section's cartridges
  // instead of failing visibly. Anything unrecognised falls back to low_profile,
  // matching the snippet's data-type default.
  const CARTRIDGE_SETS = new Map([
    ['low_profile', 'low_profile'],
    ['standard_profile', 'standard_profile'],
    ['pistol', 'low_profile'],
    ['rifle', 'standard_profile'],
  ])

  function selectCartridges(specs, type) {
    return specs[CARTRIDGE_SETS.get(type) || 'low_profile'] || []
  }

  // Smallest hole (by ascending max_diameter) that fits the given diameter.
  // Falls back to the largest hole if nothing is big enough.
  function recommendHole(holeSizes, diameter) {
    const sorted = holeSizes.slice().sort(function (a, b) {
      return a.max_diameter - b.max_diameter
    })
    for (let i = 0; i < sorted.length; i++) {
      if (diameter <= sorted[i].max_diameter) {
        return { hole: sorted[i], oversized: false }
      }
    }
    return { hole: sorted[sorted.length - 1], oversized: true }
  }

  // --- Matching a hole size to a variant option value ------------------------
  //
  // The hole names in caliber-specs.json and the option values on the product are
  // maintained in two different places (a file in the theme, and the Shopify
  // admin), so they drift. Rather than require them to be identical, a name is
  // reduced to a canonical form and tried from most to least specific. Set
  // variant_value on a hole size to name its option value outright when the two
  // can't be reconciled by shape alone.

  // Lowercase, strip everything that isn't a letter or digit. 'X-Large - 610',
  // 'x large 610' and 'XLarge610' all canonicalize to 'xlarge610'.
  function canonical(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
  }

  // The numeric code a hole name carries: 'Small - 394' -> '394'. Option values
  // are sometimes just the code.
  function holeCode(name) {
    const numbers = String(name).match(/\d{2,}/g)
    return numbers ? numbers[numbers.length - 1] : null
  }

  // The hole name with its numeric code removed: 'X-Large - 610' -> 'X-Large'.
  // Option values are sometimes just the size word.
  function holeWord(name) {
    return String(name).replace(/[\s-]*\d{2,}\s*$/, '')
  }

  // Does `haystack` contain `needle` without being glued onto a longer run of the
  // same kind of character? The point is that 'large' inside 'xlargehole' is part
  // of the longer word 'xlarge' and must not count — that's the X-Large bucket,
  // not Large — while 'large' in 'largehole' and '476' in 'hole476' both do.
  // Checked on the canonical form, which has already dropped the spaces and
  // punctuation a plain word boundary would have relied on, so only the leading
  // side can be told apart.
  function containsAtBoundary(haystack, needle) {
    const sameClass = /[a-z]/.test(needle.charAt(0)) ? /[a-z]/ : /[0-9]/
    let index = haystack.indexOf(needle)
    while (index !== -1) {
      if (index === 0 || !sameClass.test(haystack.charAt(index - 1))) return true
      index = haystack.indexOf(needle, index + 1)
    }
    return false
  }

  // The one choice in a group whose value corresponds to `needle`. Equality on
  // the canonical form wins; failing that, a single containment match at a
  // character-class boundary is accepted. Anything still ambiguous returns null
  // rather than a guess — picking the wrong bucket would quietly sell the
  // customer a block their cartridge doesn't fit, which is worse than leaving
  // the picker alone and letting them choose.
  function matchChoice(choices, needle) {
    const target = canonical(needle)
    if (!target) return null

    const exact = choices.filter(function (choice) {
      return canonical(choice.value) === target
    })
    if (exact.length) return exact[0]

    const partial = choices.filter(function (choice) {
      return containsAtBoundary(canonical(choice.value), target)
    })
    return partial.length === 1 ? partial[0] : null
  }

  // The choice standing for a hole size. An explicit variant_value is
  // authoritative — if it's set and doesn't match, that's a data error worth
  // surfacing, not something to paper over by guessing at the name instead.
  function matchHole(choices, hole) {
    if (hole.variant_value) return matchChoice(choices, hole.variant_value)
    return (
      matchChoice(choices, hole.name) ||
      matchChoice(choices, holeCode(hole.name)) ||
      matchChoice(choices, holeWord(hole.name))
    )
  }

  // --- Reaching into the variant picker --------------------------------------

  // The option groups Dawn renders inside <variant-selects>: a fieldset of radios
  // (the button and swatch pickers) or a wrapper around a select (the dropdown and
  // swatch_dropdown pickers). Same selectors Dawn's own getAllSelectedOptions
  // uses. Each group is reduced to its choices plus how to read and set them, so
  // the matching above doesn't care which picker the merchant has configured.
  // Choices are re-read on every lookup: Dawn replaces the whole <variant-selects>
  // element on each change, which makes any kept reference stale.
  function optionGroups(variantSelects) {
    const groups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown')
    return Array.from(groups)
      .map(function (group) {
        const select = group.querySelector('select')
        if (select) {
          return {
            choices: Array.from(select.options),
            isSelected: function (option) {
              return select.value === option.value
            },
            // Dawn moves the `selected` attribute itself while handling the
            // change event, so only the value is set here.
            apply: function (option) {
              select.value = option.value
              return select
            },
          }
        }
        return {
          choices: Array.from(group.querySelectorAll('input[type="radio"]')),
          isSelected: function (input) {
            return input.checked
          },
          apply: function (input) {
            input.checked = true
            return input
          },
        }
      })
      .filter(function (group) {
        return group.choices.length
      })
  }

  // Dawn re-renders the section after an option change and then restores focus
  // with document.querySelector('#' + target.id), scrolling the variant picker
  // into view. That's right for an option the customer clicked and wrong for one
  // selected on their behalf — the page would jump away from the dropdown they're
  // still using. product-info reads that id synchronously while the change event
  // is dispatching, so standing an id in that the re-rendered markup will never
  // contain turns the focus call into a no-op. The real id goes back immediately;
  // the node itself is discarded by the re-render.
  const FOCUS_SENTINEL_ID = 'CaliberCalculatorNoFocus'

  function dispatchChangeWithoutFocus(eventTarget, focusCarrier) {
    const originalId = focusCarrier.getAttribute('id')
    focusCarrier.setAttribute('id', FOCUS_SENTINEL_ID)
    try {
      eventTarget.dispatchEvent(new Event('change', { bubbles: true }))
    } finally {
      if (originalId === null) focusCarrier.removeAttribute('id')
      else focusCarrier.setAttribute('id', originalId)
    }
  }

  class CaliberCalculator extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-caliber-select]')
      this.result = this.querySelector('[data-caliber-result]')
      if (!this.select || !this.result) return

      // The variant picker is a sibling of this control inside <product-info>.
      // Absent on a product with no options, in which case there is nothing to
      // select and the control stays purely informational.
      this.productInfo = this.closest('product-info')
      this.recommendation = null
      this.selectedValue = null
      this.applyingSelection = false

      const url = this.dataset.specsUrl
      const type = this.dataset.type || 'low_profile'

      loadSpecs(url)
        .then((specs) => {
          const cartridges = selectCartridges(specs, type)
          if (!cartridges.length) throw new Error('No cartridges for type: ' + type)
          this.holeSizes = specs.hole_sizes || []
          this.byCartridge = {}
          this.populate(cartridges)
          this.select.addEventListener('change', this.onChange.bind(this))
          this.watchManualChanges()
        })
        .catch((error) => {
          // Fail quietly — don't block the page. Hide the control so it isn't left broken.
          console.error('[caliber-calculator]', error)
          this.hidden = true
        })
    }

    disconnectedCallback() {
      if (this.onManualChange) {
        this.productInfo?.removeEventListener('change', this.onManualChange)
        this.onManualChange = null
      }
    }

    populate(cartridges) {
      const fragment = document.createDocumentFragment()
      // Cartridge names start with numbers in several inconsistent formats (9mm,
      // .38, 7.62 x 25), so alphabetical/numeric sorting on the name isn't useful.
      // Sort by physical dimensions instead: bullet_diameter, then effective base
      // (max of base/rim diameter), then overall_length, falling back to a
      // numeric-aware name compare on ties.
      const sorted = cartridges.slice().sort(function (a, b) {
        return (
          compareDimension(a.bullet_diameter, b.bullet_diameter) ||
          compareNumbers(effectiveBase(a), effectiveBase(b)) ||
          compareDimension(a.overall_length, b.overall_length) ||
          String(a.cartridge).localeCompare(String(b.cartridge), undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        )
      })
      sorted.forEach((entry) => {
        const name = entry.cartridge
        // De-dupe repeated cartridge names.
        if (!name || this.byCartridge[name]) return
        this.byCartridge[name] = entry
        const option = document.createElement('option')
        option.value = name
        option.textContent = name
        fragment.appendChild(option)
      })
      this.select.appendChild(fragment)
    }

    onChange() {
      const entry = this.byCartridge[this.select.value]
      const diameter = entry ? effectiveBase(entry) : NaN

      if (isNaN(diameter)) {
        this.recommendation = null
        this.selectedValue = null
        this.render()
        return
      }

      this.recommendation = recommendHole(this.holeSizes, diameter)
      // An oversized case has no hole to select — it's a conversation, not a
      // purchase — so the picker is left exactly as the customer had it.
      this.selectedValue = this.recommendation.oversized ? null : this.autoSelect(this.recommendation.hole)
      this.render()
    }

    get variantSelects() {
      return this.productInfo ? this.productInfo.querySelector('variant-selects') : null
    }

    // Select the option value standing for `hole`, returning it, or null when
    // there's nothing to select: a product with no options, or option values this
    // hole can't be mapped onto. The first group that yields a match is the one
    // used, which on these products is the hole size; any other option (a colour,
    // say) won't match a hole name and is left alone.
    autoSelect(hole) {
      const variantSelects = this.variantSelects
      if (!variantSelects) return null

      const groups = optionGroups(variantSelects)
      for (let i = 0; i < groups.length; i++) {
        const group = groups[i]
        const choice = matchHole(group.choices, hole)
        if (!choice) continue

        // Already on it — don't make Dawn re-render the section to tell it
        // something it knows.
        if (group.isSelected(choice)) return choice.value

        const eventTarget = group.apply(choice)
        const focusCarrier =
          eventTarget.tagName === 'SELECT' ? eventTarget.selectedOptions[0] || eventTarget : eventTarget

        // Flagged so watchManualChanges can tell our own change event from one
        // the customer caused.
        this.applyingSelection = true
        try {
          dispatchChangeWithoutFocus(eventTarget, focusCarrier)
        } finally {
          this.applyingSelection = false
        }
        return choice.value
      }

      if (groups.length) {
        console.warn(
          '[caliber-calculator] no variant option value matches hole size "' +
            hole.name +
            '" — set variant_value on it in caliber-specs.json to map it explicitly',
        )
      }
      return null
    }

    // The customer can still change the hole size by hand after we've set it.
    // Drop the confirmation when they do, so the page never claims to have
    // selected something that isn't selected any more. The recommendation itself
    // stays up — it's still the right answer for their cartridge.
    watchManualChanges() {
      if (!this.productInfo || this.onManualChange) return

      this.onManualChange = (event) => {
        if (this.applyingSelection || !this.selectedValue) return
        // Only option changes count. product-info also fires a change on the
        // form's hidden variant id input, which would otherwise look like the
        // customer having overridden us. Asked of the event target rather than
        // of the current <variant-selects>, because by the time this runs Dawn
        // may already have swapped that element for a freshly rendered one —
        // which wouldn't contain the node the event came from.
        if (!event.target.closest || !event.target.closest('variant-selects')) return
        if (this.isSelected(this.selectedValue)) return
        this.selectedValue = null
        this.render()
      }

      this.productInfo.addEventListener('change', this.onManualChange)
    }

    // Is `value` still the selected option value in its group? Read at change
    // time, before Dawn re-renders, which is fine: the browser has already
    // updated checked / select.value by the time the event fires.
    isSelected(value) {
      const variantSelects = this.variantSelects
      if (!variantSelects) return false

      return optionGroups(variantSelects).some(function (group) {
        return group.choices.some(function (choice) {
          return group.isSelected(choice) && canonical(choice.value) === canonical(value)
        })
      })
    }

    render() {
      this.result.textContent = ''

      if (!this.recommendation) {
        this.result.hidden = true
        return
      }

      this.result.hidden = false

      if (this.recommendation.oversized) {
        this.result.textContent =
          'This cartridge is larger than our standard holes — please contact us for a custom size.'
        return
      }

      this.result.append('Recommended hole size: ')
      const name = document.createElement('strong')
      name.textContent = this.recommendation.hole.name
      this.result.appendChild(name)

      if (this.selectedValue) {
        const note = document.createElement('span')
        note.className = 'caliber-calculator__selected'
        note.textContent = 'Selected for you'
        this.result.appendChild(note)
      }
    }
  }

  if (!customElements.get('caliber-calculator')) {
    customElements.define('caliber-calculator', CaliberCalculator)
  }
})()
